// src/lib/api/shared/helpers.js
import { supabase } from '../../supabase'

// ============================================================================
// SINGLE SOURCE OF TRUTH FOR COMPANY ID CACHE
// This cache is set ONCE by the auth provider and used by ALL API functions
// ============================================================================

let companyIdCache = {
  value: null,
  timestamp: 0,
  userId: null
}

// Cache for 30 minutes (like a session token)
const CACHE_DURATION = 30 * 60 * 1000

// Timeout for API calls to prevent indefinite hangs
const API_TIMEOUT_MS = 10000 // 10 seconds

// Helper to add timeout to promises. Exported for callers that need to bound
// supabase operations (e.g. the Schedule Estimate pipeline, which can be
// stuck for tens of seconds on the first request after a tab-back wake-up
// because the queued query-cache invalidations / auth Web Lock can serialize
// behind in-flight refetches).
export const withTimeout = (promise, ms, operationName) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${operationName} timeout after ${ms}ms`)), ms)
    )
  ])
}

// ============================================================================
// REQUEST DEDUPLICATION
// Prevents 30+ parallel API calls from each making separate auth requests
// If a fetch is in progress, subsequent calls share the same Promise
// ============================================================================
let pendingCompanyIdFetch = null

/**
 * Get cached company ID (synchronous, instant)
 * Returns null if not cached or expired
 */
export const getCachedCompanyId = () => {
  const now = Date.now()
  if (companyIdCache.value && 
      companyIdCache.userId && 
      (now - companyIdCache.timestamp) < CACHE_DURATION) {
    return companyIdCache.value
  }
  return null
}

/**
 * Get cached user ID (synchronous, instant)
 */
export const getCachedUserId = () => {
  return companyIdCache.userId
}

/**
 * Set company ID cache from auth provider
 * Called ONCE when user authenticates - all API calls then use this cached value
 */
export const setCompanyIdCache = (companyId, userId) => {
  if (companyId && userId) {
    companyIdCache.value = companyId
    companyIdCache.userId = userId
    companyIdCache.timestamp = Date.now()
    console.log('[helpers] Company ID cached:', companyId, 'for user:', userId)
  }
}

/**
 * Internal function to actually fetch company ID from auth/database
 * Called only when cache is empty AND no fetch is in progress
 */
const _fetchCompanyId = async () => {
  try {
    // Get user with timeout protection
    const { data: { user }, error: userError } = await withTimeout(
      supabase.auth.getUser(),
      API_TIMEOUT_MS,
      'Auth getUser'
    )

    if (userError || !user) {
      return null
    }

    // Check user metadata first (no DB call needed)
    const metadataCompanyId = user.user_metadata?.company_id
    if (metadataCompanyId) {
      setCompanyIdCache(metadataCompanyId, user.id)
      return metadataCompanyId
    }

    // Fallback: Query database for company ID with timeout
    const { data: accessData } = await withTimeout(
      supabase
        .from('user_access_cache')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle(),
      API_TIMEOUT_MS,
      'user_access_cache query'
    )

    if (accessData?.company_id) {
      setCompanyIdCache(accessData.company_id, user.id)
      return accessData.company_id
    }

    // Last resort: Check companies table for owners with timeout
    const { data: companyData } = await withTimeout(
      supabase
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .single(),
      API_TIMEOUT_MS,
      'companies query'
    )

    if (companyData?.id) {
      setCompanyIdCache(companyData.id, user.id)
      return companyData.id
    }

    return null
  } catch (error) {
    console.error('[helpers] Error getting company ID:', error.message)
    return null
  }
}

/**
 * Get company ID for the current authenticated user
 * FAST PATH: Returns cached value instantly (99% of calls)
 * SLOW PATH: Fetches from auth - with REQUEST DEDUPLICATION
 *
 * If 30 parallel API calls all hit this function, they share ONE fetch
 * instead of each making separate supabase.auth.getUser() calls
 */
export const getCompanyIdForUser = async () => {
  // FAST PATH: Return cached value immediately
  const cached = getCachedCompanyId()
  if (cached) {
    return cached
  }

  // DEDUPLICATION: If a fetch is already in progress, wait for it
  if (pendingCompanyIdFetch) {
    console.log('[helpers] Company ID fetch already in progress, waiting...')
    return pendingCompanyIdFetch
  }

  // SLOW PATH: Cache miss - start new fetch (only ONE even if 30 calls arrive)
  console.warn('[helpers] Company ID cache miss, fetching from auth...')

  // Store the promise so parallel calls can share it
  pendingCompanyIdFetch = _fetchCompanyId().finally(() => {
    // Clear the pending fetch when done (success or error)
    pendingCompanyIdFetch = null
  })

  return pendingCompanyIdFetch
}

/**
 * Clear the company ID cache (call on logout)
 */
export const clearHelperCompanyIdCache = () => {
  companyIdCache = {
    value: null,
    timestamp: 0,
    userId: null
  }
  console.log('[helpers] Company ID cache cleared')
}

/**
 * Get validated user ID. Prefers the cached user id populated by the auth
 * provider; falls back to a guarded supabase.auth.getUser() only when the
 * cache is cold.
 *
 * Why: supabase-js v2 serializes auth ops behind navigator.locks. If the
 * browser suspends the auto-refresh timer mid-lock during a long tab-hidden
 * interval, subsequent getUser() calls deadlock until the page reloads.
 * Reading the cached id skips the lock on the hot path; the 5s race surfaces
 * the cold-path hang as an error instead of an infinite spinner. Same shape
 * as _createJob (commit 3392704).
 */
export const getValidatedUserId = async () => {
  const cached = getCachedUserId()
  if (cached) return cached

  try {
    const { data: { user }, error } = await Promise.race([
      supabase.auth.getUser(),
      new Promise((_, reject) => setTimeout(
        () => reject(new Error('Authentication check timed out — please refresh the page')),
        5000
      ))
    ])
    if (error || !user?.id) return null
    return user.id
  } catch (error) {
    console.error('[helpers] getValidatedUserId failed:', error.message)
    return null
  }
}

/**
 * Get validated company ID with error handling
 * Extracted from original supabaseApi.js lines 27-45
 */
export const getValidatedCompanyId = async () => {
  try {
    const companyId = await getCompanyIdForUser()
    
    if (!companyId) {
      // Only warn once per minute to avoid log spam
      if (!getValidatedCompanyId._lastWarn || Date.now() - getValidatedCompanyId._lastWarn > 60000) {
        console.warn('[supabaseApi] User not associated with any company')
        getValidatedCompanyId._lastWarn = Date.now()
      }
      return null
    }
    
    return companyId
  } catch (error) {
    console.error('[supabaseApi] Error in getValidatedCompanyId:', error.message)
    return null
  }
}

/**
 * Standard error response format
 */
export const createErrorResponse = (message, code = null) => {
  return {
    data: [],
    error: {
      message,
      code,
      timestamp: new Date().toISOString()
    }
  }
}

/**
 * Standard success response format
 */
export const createSuccessResponse = (data) => {
  return {
    data: Array.isArray(data) ? data : [data],
    error: null
  }
}

/**
 * Get current authenticated user
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) {
      console.error('[supabaseApi] Error getting user:', error)
      return null
    }
    return user
  } catch (error) {
    console.error('[supabaseApi] Error getting current user:', error)
    return null
  }
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  const user = await getCurrentUser()
  return user !== null
}